import { useTranslation } from 'react-i18next';
import {
  entradaDecimal,
  formatoDecimal,
  formatoEuros,
  type LiquidacionSnapshot,
} from '@/lib/liquidacion';
import type { CamadaLiquidacionResumen } from '@/lib/types';

export function LiquidacionImportes({ resultado }: { resultado: CamadaLiquidacionResumen }) {
  const { t } = useTranslation();
  const conceptos = [
    'bruto_eur',
    'iva_eur',
    'importe_bajas_eur',
    'importe_carga_eur',
    'bonificacion_eur',
  ] as const;
  return (
    <dl className="space-y-2 text-sm tabular-nums">
      {conceptos.map((campo) => (
        <div key={campo} className="flex justify-between gap-4">
          <dt className="text-text-secondary">{t(`liquidacion.${campo}`)}</dt>
          <dd className="text-right font-medium">{formatoEuros(resultado[campo])}</dd>
        </div>
      ))}
      <div className="flex justify-between gap-4 border-t border-border pt-3 font-semibold">
        <dt>{t('liquidacion.total')}</dt>
        <dd className="text-right">{formatoEuros(resultado.total_eur)}</dd>
      </div>
    </dl>
  );
}

export function LiquidacionDesglose({ snapshot }: { snapshot: LiquidacionSnapshot }) {
  const { t } = useTranslation();
  const r = snapshot.resultado;
  const parametros = snapshot.configuracion.sexos[snapshot.datos.sexo];
  const indicadores = [
    [t('liquidacion.sexo'), t(`pienso_ajustes.${r.sexo}`)],
    [t('liquidacion.dias_engorde'), String(r.dias_engorde)],
    [t('liquidacion.edad_total'), `${r.edad_total} ${t('liquidacion.dias')}`],
    [t('liquidacion.kg_ganados'), `${formatoDecimal(r.kg_ganados)} kg`],
    [t('liquidacion.toneladas'), `${formatoDecimal(r.toneladas_ganadas, 3)} t`],
    [t('liquidacion.conversion'), formatoDecimal(r.conversion, 6)],
    [t('liquidacion.bajas_pct'), `${formatoDecimal(r.porcentaje_bajas, 4)} %`],
    [t('liquidacion.referencia'), `${formatoDecimal(r.porcentaje_referencia)} %`],
  ];
  return (
    <section aria-label={t('liquidacion.desglose')} className="space-y-5">
      <div className="rounded-lg border border-border bg-surface p-4">
        <LiquidacionImportes resultado={r} />
      </div>
      <dl className="grid grid-cols-2 gap-x-4 gap-y-3 text-sm">
        {indicadores.map(([label, value]) => (
          <div key={label} className="min-w-0">
            <dt className="text-text-secondary">{label}</dt>
            <dd className="break-words font-medium tabular-nums">{value}</dd>
          </div>
        ))}
      </dl>
      <details className="border-t border-border pt-3 text-sm">
        <summary className="cursor-pointer font-medium">
          {t('liquidacion.parametros_aplicados')}
        </summary>
        <dl className="mt-3 space-y-2">
          {(['base', 'complemento', 'coef_dias', 'coef_conversion', 'coef_bajas'] as const).map(
            (key) => (
              <div className="flex justify-between gap-3" key={key}>
                <dt className="text-text-secondary">{t(`liquidacion.${key}`)}</dt>
                <dd className="min-w-0 break-all text-right tabular-nums">
                  {entradaDecimal(parametros[key])}
                </dd>
              </div>
            ),
          )}
          <div className="flex justify-between">
            <dt>{t('liquidacion.iva_pct')}</dt>
            <dd>{formatoDecimal(snapshot.configuracion.porcentaje_iva)} %</dd>
          </div>
          <div className="flex justify-between gap-3">
            <dt>{t('liquidacion.tramo_aplicado')}</dt>
            <dd>
              {r.tramo_aplicado.edad_min}–{r.tramo_aplicado.edad_max ?? t('liquidacion.sin_limite')}{' '}
              {t('liquidacion.dias')}
            </dd>
          </div>
        </dl>
      </details>
    </section>
  );
}
